import api from '../../../services/api';

/**
 * Normalizes raw backend user object from GET /rfp/auth/users
 * Backend schema:
 * {
 *   _id: string,
 *   email: string,
 *   first_name: string,
 *   last_name: string,
 *   employee_name: string,
 *   designation: string,
 *   department: string,
 *   location: string,
 *   phone_number: string,
 *   role: "admin" | "team_lead" | "employee",
 *   status: "pending" | "active" | "inactive",
 *   assigned: number,
 *   manager_id: string,
 *   manager_name: string,
 *   avatar_url: string
 * }
 */
export const normalizeUserData = (u) => {
    if (!u) return null;

    const id = u._id || u.id || `user-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const firstName = u.first_name || '';
    const lastName = u.last_name || '';
    const combinedName = `${firstName} ${lastName}`.trim();
    const name = u.employee_name || (combinedName.length > 0 ? combinedName : (u.name || u.email || 'User'));

    const rawRole = (u.role || '').toLowerCase();
    let roleLabel = 'Team Member';
    let avatarColor = '#8B5CF6';

    if (rawRole.includes('admin')) {
        roleLabel = 'Admin';
        avatarColor = '#7C3AED';
    } else if (rawRole.includes('lead') || rawRole.includes('manager')) {
        roleLabel = 'Team Lead';
        avatarColor = '#EA580C';
    } else {
        roleLabel = 'Team Member';
        avatarColor = '#8B5CF6';
    }

    // Initials derivation
    const initials = name
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .substring(0, 2)
        .toUpperCase() || 'U';

    // Format created date if available
    let created = u.created || u.created_at || u.joined_date || '';
    if (created && typeof created === 'string' && created.includes('T')) {
        try {
            const dateObj = new Date(created);
            if (!isNaN(dateObj.getTime())) {
                created = dateObj.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
            }
        } catch {
            // Keep original string if parsing fails
        }
    }
    if (!created) {
        created = 'Aug 1, 2026';
    }

    const rawStatus = (u.status || 'active').toLowerCase();
    const status = rawStatus === 'pending' ? 'pending' : (rawStatus === 'inactive' ? 'inactive' : 'active');
    const statusLabel = status === 'active' ? 'Approved' : (status.charAt(0).toUpperCase() + status.slice(1));

    return {
        id: String(id),
        _id: String(u._id || id),
        name,
        first_name: firstName,
        last_name: lastName,
        email: u.email || '',
        phone_number: u.phone_number || '',
        role: roleLabel,
        rawRole: u.role || 'employee',
        designation: u.designation || (roleLabel === 'Team Lead' ? 'AI Engineer' : 'Team Member'),
        department: u.department || 'AI',
        location: u.location || '',
        status,
        status_label: statusLabel,
        activeProposals: typeof u.assigned === 'number' ? u.assigned : (u.activeProposals || 0),
        assigned: typeof u.assigned === 'number' ? u.assigned : 0,
        created,
        joined_date: created,
        initials,
        avatarColor: u.avatarColor || avatarColor,
        avatar_url: u.avatar_url || '',
        reports_to: u.manager_name || u.reports_to || (roleLabel === 'Team Lead' ? 'Executive Director' : 'Shreya Rai'),
        manager_id: u.manager_id || null,
        manager_name: u.manager_name || null,
        organization: u.organization || 'Exalogic Consulting',
        direct_reports: u.direct_reports || []
    };
};

const MAX_PAGE_SIZE = 100; // the largest page_size the server accepts
const MAX_PAGES_TO_READ = 50; // safety stop when reading every page

/**
 * Fetch one page of users, sorted by name, with organizational hierarchy metadata
 * GET /rfp/auth/users?role=&manager_id=&page=&page_size=
 *
 * @param {Object} [params] - Optional query parameters
 * @param {string} [params.role] - Filter by role: 'admin' | 'team_lead' | 'employee'
 * @param {string} [params.manager_id] - Filter by manager ID
 * @param {number} [params.page] - Page number, starting at 1 (server default 1)
 * @param {number} [params.page_size] - Users per page, 2 to 100 (server default 10)
 * @returns {Promise} Axios response: { items, total, page, page_size, total_pages }
 */
export const fetchAllUsers = async (params = {}) => {
    const queryParams = {};
    if (params.role) queryParams.role = params.role;
    if (params.manager_id) queryParams.manager_id = params.manager_id;
    if (params.page) queryParams.page = params.page;
    if (params.page_size) queryParams.page_size = params.page_size;

    return api.get('/rfp/auth/users', { params: queryParams });
};

/**
 * Reads the answer of GET /rfp/auth/users. Also accepts a plain array (the older answer).
 * @returns {{ items: object[], total: number, page: number, pageSize: number, totalPages: number }}
 */
export const readUsersPage = (response) => {
    const data = response?.data;
    const items = Array.isArray(data) ? data : ([data?.items, data?.users, data?.data].find(Array.isArray) || []);
    const total = data?.total !== undefined && Number.isFinite(Number(data.total)) ? Number(data.total) : items.length;
    const pageSize = Number(data?.page_size) || items.length || 1;
    const totalPages = Number(data?.total_pages) || Math.max(1, Math.ceil(total / pageSize));
    const page = Number(data?.page) || 1;
    return { items, total, page, pageSize, totalPages };
};

/**
 * Every user that matches the filters, read page by page. For pickers and look-ups that need the
 * whole list, not one screen of it.
 * @returns {Promise<object[]>} the raw users
 */
export const fetchEveryUser = async (params = {}) => {
    const everyone = [];
    let page = 1;
    let totalPages = 1;
    do {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetchAllUsers({ ...params, page, page_size: MAX_PAGE_SIZE });
        const result = readUsersPage(response);
        everyone.push(...result.items);
        totalPages = result.totalPages;
        page += 1;
    } while (page <= totalPages && page <= MAX_PAGES_TO_READ);
    return everyone;
};

/**
 * Team structure as seen from one user
 * GET /rfp/auth/users/{user_id}/org-tree
 *
 * What comes back depends on who `userId` is: an admin gets every team lead with their members,
 * a team lead gets the admins and only their own team, an employee gets only their team lead and
 * themselves. Non-admins can only ask about themselves.
 *
 * 200: { admins: [{ id, name, role, avatar_url, status }],
 *        team_leads: [{ id, name, role, avatar_url, status, member_count, members: [{ ...same }] }] }
 * 403: only admins can open someone else's tree.  404: user not found.  422: validation error.
 */
export const fetchOrgTree = async (userId) => {
    return api.get(`/rfp/auth/users/${encodeURIComponent(userId)}/org-tree`);
};

/**
 * Register / Add a new user (Admin only)
 * POST /rfp/auth/register
 *
 * @param {Object} userData - User registration payload
 * @param {string} userData.email - User email
 * @param {string} userData.first_name - User first name
 * @param {string} userData.last_name - User last name
 * @param {string} userData.phone_number - User phone number
 * @param {string} userData.password - User password
 * @param {string} [userData.role] - User role ('employee' | 'team_lead' | 'admin')
 * @param {string} [userData.designation] - User designation
 * @param {string} [userData.department] - User department
 * @param {string} [userData.location] - User location
 * @returns {Promise} Axios response (201 Created)
 */
export const registerUser = async (userData) => {
    return api.post('/rfp/auth/register', userData);
};

/**
 * Normalizes role items from GET /rfp/auth/roles
 * Handles both string arrays ["admin", "team_lead", "employee"]
 * and object arrays [{ value: "team_lead", label: "Team Lead", code: 1 }]
 */
export const normalizeRoleItem = (item) => {
    if (!item) return null;
    if (typeof item === 'string') {
        const val = item.toLowerCase();
        let label = 'Team Member (Employee)';
        if (val === 'admin') label = 'Admin';
        else if (val === 'team_lead') label = 'Team Lead';
        else if (val === 'employee') label = 'Team Member (Employee)';
        else {
            label = val.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        }
        return { value: item, label, code: item };
    }
    if (typeof item === 'object') {
        const value = item.value || item.code || item.role || '';
        let label = item.label || item.name || '';
        if (!label) {
            if (value === 'admin') label = 'Admin';
            else if (value === 'team_lead') label = 'Team Lead';
            else if (value === 'employee') label = 'Team Member (Employee)';
            else label = String(value);
        }
        return {
            value: String(value),
            label: String(label),
            code: item.code ?? value
        };
    }
    return null;
};

/**
 * Fetch all available roles for dropdowns
 * GET /rfp/auth/roles
 * @returns {Promise} Axios response
 */
export const fetchRoles = async () => {
    return api.get('/rfp/auth/roles');
};

/**
 * Permanently delete a user (Admin only)
 * DELETE /rfp/auth/users/{user_id}
 *
 * @param {string} userId - User ID to delete
 * @returns {Promise} Axios response (204 No Content)
 */
export const deleteUser = async (userId) => {
    return api.delete(`/rfp/auth/users/${userId}`);
};

/**
 * Bulk assigns multiple employees to the Team Lead in the URL (Admin only)
 * POST /team/{team_lead_id}/assign-members
 *
 * @param {string} teamLeadId - Team Lead ID
 * @param {string[]} employeeIds - Array of employee IDs to assign
 * @returns {Promise} Axios response
 */
export const assignMembersToLead = async (teamLeadId, employeeIds) => {
    return api.post(`/team/${teamLeadId}/assign-members`, { employee_ids: employeeIds });
};



